import cx from 'classnames'
import { useContext } from 'react'

import { SanityLinkFragment } from '@data/sanity/queries/types/link'
import { SanityMenuFeaturedLinkFragment } from '@data/sanity/queries/types/site'
import { SanityLink } from '@data/sanity/queries/types/content'
import { getPageUrl, PageType } from '@lib/routes'
import { UserContext } from '@lib/user/context'

import AccountButton from '@components/buttons/account-button'
import LanguageSwitch from '@components/language-switch'
import CustomLink from '@components/link'
import Dropdown from './dropdown'
import MegaNavButton from './mega-navigation-button'

export type ItemType = SanityLinkFragment | SanityMenuFeaturedLinkFragment

interface MenuItemProps {
  item: ItemType
  hasFocus: boolean
  useMegaNav: boolean
  onClick?: () => void
  isFooterMenuItem?: boolean
  isHeaderDesktopMenuItem?: boolean
  isHeaderMobilePrimaryMenuItem?: boolean
  isHeaderMobileSecondaryMenuItem?: boolean
  isMegaNavMenuItem?: boolean
}

const MenuItem = ({
  item,
  hasFocus,
  useMegaNav,
  onClick,
  isFooterMenuItem,
  isHeaderDesktopMenuItem,
  isHeaderMobilePrimaryMenuItem,
  isHeaderMobileSecondaryMenuItem,
  isMegaNavMenuItem,
}: MenuItemProps) => {
  const { user } = useContext(UserContext)

  const title = 'title' in item && item.title ? item.title : ''

  const itemClassNames = cx({
    'text-yellow text-5xl font-serif inline-block py-1': isFooterMenuItem,
    'block font-medium text-base p-2': isHeaderDesktopMenuItem,
    'font-serif block p-0 text-5xl text-left py-1':
      isHeaderMobilePrimaryMenuItem,
    'font-serif block p-0 text-3xl text-left': isHeaderMobileSecondaryMenuItem,
    'font-serif inline-block text-5xl leading-normal hover:scale-[1.05] transition-all':
      isMegaNavMenuItem,
  })

  // Language switch
  if (item._type === 'navLanguageSwitch') {
    return (
      <LanguageSwitch
        id={item._key}
        buttonClassName={itemClassNames}
        hasCurrency={item.displayCurrency}
      />
    )
  }

  // Account button
  if (item._type === 'navAccountButton') {
    const accountUrl = getPageUrl(
      user?.isLoggedIn ? PageType.ACCOUNT_PAGE : PageType.LOGIN_PAGE,
    )

    return (
      <AccountButton
        id={`account-button-${item._key}`}
        className={cx(itemClassNames, { 'p-0': !item.text })}
        showIcon={item.displayIcon}
        href={accountUrl}
      >
        {item.text}
      </AccountButton>
    )
  }

  // Dropdown list
  if (item._type === 'navDropdown') {
    const dropdownItems = (item as SanityMenuFeaturedLinkFragment).dropdownItems

    if (useMegaNav) {
      return (
        <MegaNavButton
          id={item._key}
          title={title}
          className={itemClassNames}
        />
      )
    }

    if (!dropdownItems) {
      return null
    }

    return (
      <Dropdown
        id={item._key}
        title={title}
        items={dropdownItems}
        onClick={onClick}
        listItemClassName={cx({
          'mx-2 my-0': isHeaderDesktopMenuItem,
          'my-3':
            isHeaderMobilePrimaryMenuItem || isHeaderMobileSecondaryMenuItem,
        })}
        listItemLinkClassName={itemClassNames}
        buttonClassName={itemClassNames}
      />
    )
  }

  // Single link
  return (
    <CustomLink
      link={item as SanityLink}
      onClick={onClick}
      tabIndex={!hasFocus ? -1 : 0}
      className={itemClassNames}
      showCollectionCount={isMegaNavMenuItem}
    >
      {title}
    </CustomLink>
  )
}

export default MenuItem
